import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Bot, FileDown, Send, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Section, Spinner } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { useWsData } from "@/hooks/useWsData";
import { api, BACKEND, download, errMsg } from "@/lib/api";

const QUICK = [
  "Faça uma análise completa do meu orçamento mensal",
  "Qual dívida devo quitar primeiro e por quê?",
  "Monte minha estratégia para os próximos 12 meses, 5 anos e 30 anos",
  "Como acelerar minha reserva de emergência?",
];
const STRATEGY = "Com base no meu diagnóstico, crie uma estratégia financeira detalhada para 12 meses, 5 anos e 30 anos, com ações mensais concretas.";

function Consent({ onOk }) {
  const { refresh } = useAuth();
  const accept = async () => {
    await api.put("/me/consents", { ai_consent: true });
    await refresh();
    onOk();
  };
  return (
    <Section testid="ai-consent">
      <ShieldCheck className="h-8 w-8 text-[#D4AF37]" />
      <h3 className="mt-3 text-lg font-semibold">Consentimento para o Mentor IA (LGPD)</h3>
      <p className="mt-2 text-sm text-slate-400">Para gerar respostas personalizadas, um resumo dos números calculados deste espaço (sem seu e-mail) será enviado ao provedor de IA (OpenAI). Os cálculos são feitos pelo motor PROSPERA+. Você pode revogar a qualquer momento em Privacidade & LGPD.</p>
      <Button className="mt-5" onClick={accept} data-testid="ai-consent-accept-btn">Autorizo o uso do Mentor IA</Button>
    </Section>
  );
}

export default function Mentor() {
  const { data, reload, wid } = useWsData("/mentor/messages");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const end = useRef(null);

  useEffect(() => { if (data) setMsgs(data.messages); }, [data]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => {
    if (data?.consent && params.get("prompt") === "estrategia" && !busy) { setParams({}); send(STRATEGY); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const send = async (message) => {
    if (!message.trim() || busy) return;
    setBusy(true);
    setText("");
    setMsgs((m) => [...m, { id: `u${Date.now()}`, role: "user", content: message }, { id: "streaming", role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND}/api/workspaces/${wid}/mentor/stream`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("pp_token") || ""}` },
        body: JSON.stringify({ message }),
      });
      if (!res.ok) throw { response: { data: await res.json() } };
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          const ev = JSON.parse(line);
          if (ev.error) toast.error(ev.error);
          if (ev.rollback) setMsgs((m) => m.filter((x) => x.id !== "streaming" && !(x.role === "user" && x.content === message && String(x.id).startsWith("u"))));
          if (ev.delta) setMsgs((m) => m.map((x) => (x.id === "streaming" ? { ...x, content: x.content + ev.delta } : x)));
        }
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      reload();
    }
  };

  const pdf = async (id) => {
    try { await download(`/workspaces/${wid}/mentor/messages/${id}/pdf`, "prospera-mentor.pdf"); toast.success("PDF gerado"); } catch (e) { toast.error(errMsg(e)); }
  };
  const clear = async () => { await api.delete(`/workspaces/${wid}/mentor/messages`); reload(); };

  if (!data) return <Spinner />;
  return (
    <div data-testid="mentor-page">
      <PageHeader eyebrow="Inteligência artificial" title="Mentor financeiro" subtitle="Orientação educacional baseada nos seus números reais, sem promessas de retorno.">
        <span className="rounded-full border border-[#1E3A6E] px-3 py-1.5 text-xs text-slate-300" data-testid="ai-usage">{data.used}/{data.limit} mensagens este mês</span>
        <Button variant="ghost" size="sm" onClick={clear} data-testid="mentor-clear-btn"><Trash2 className="mr-1 h-4 w-4" />Limpar</Button>
      </PageHeader>
      {!data.consent ? <Consent onOk={reload} /> : (
        <div className="card-p flex h-[68vh] flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto p-4 md:p-6" data-testid="mentor-messages">
            {!msgs.length && (
              <div className="py-6 text-center">
                <Bot className="mx-auto h-10 w-10 text-[#D4AF37]" />
                <p className="mt-3 text-sm text-slate-400">Escolha uma pergunta ou escreva a sua.</p>
                <div className="mx-auto mt-5 grid max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
                  {QUICK.map((q, i) => <button key={q} onClick={() => send(q)} className="rounded-lg border border-[#1E3A6E] bg-[#0B1A33] p-3 text-left text-sm hover:border-[#D4AF37]/60" data-testid={`quick-prompt-${i}`}>{q}</button>)}
                </div>
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
                <div className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm md:max-w-[75%] ${m.role === "user" ? "bg-[#D4AF37] text-[#0B1A33]" : "border border-[#1E3A6E] bg-[#0B1A33]"}`}>
                  {m.role === "user" ? m.content : <div className="prose-mentor">{m.content ? <ReactMarkdown>{m.content}</ReactMarkdown> : <span className="animate-pulse text-slate-400">Analisando seus números...</span>}</div>}
                  {m.role === "assistant" && m.id !== "streaming" && (
                    <button onClick={() => pdf(m.id)} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 px-3 py-1 text-xs text-[#F5D77F] transition-colors hover:bg-[#D4AF37]/15" data-testid={`mentor-pdf-${m.id}`}>
                      <FileDown className="h-3.5 w-3.5" /> Gerar PDF para impressão
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex gap-2 border-t border-[#1E3A6E] p-3">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Pergunte ao mentor..." rows={1} className="min-h-[44px] resize-none bg-[#0B1A33]"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(text); } }} data-testid="mentor-input" />
            <Button type="submit" disabled={busy || !text.trim()} data-testid="mentor-send-btn"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </div>
  );
}
